import React, { Suspense, lazy } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Helmet } from "react-helmet";
import { Analytics } from '@vercel/analytics/react';
import HomePage from './pages/HomePage';
import TeamSelection from './pages/TeamSelection';
import BugReportButton from './components/BugReportButton';
import CoffeeSupportBanner from './components/CoffeeSupportBanner';
import AnalyticsConsent from './components/AnalyticsConsent';
import { trackGaPageView } from './utils/analytics';
import { isNoIndexRoute, normalizeIndexingPath } from './seo/indexingPolicy';

const SITE_URL = 'https://www.squadverse.uk';

function lazyRetry(importFn, key) {
  return new Promise((resolve, reject) => {
    const storageKey = `lazy-retry-${key}`;
    let alreadyRetried = false;
    let retryStorageAvailable = true;
    try { alreadyRetried = sessionStorage.getItem(storageKey) === 'true'; } catch (_) { retryStorageAvailable = false; }

    importFn()
      .then((module) => {
        try { sessionStorage.setItem(storageKey, 'false'); } catch (_) {}
        resolve(module);
      })
      .catch((error) => {
        const isChunkLoadError = /ChunkLoadError|Loading chunk/i.test(String(error && error.message ? error.message : error));
        if (!alreadyRetried && isChunkLoadError && retryStorageAvailable) {
          // Reload only after recording the retry, so blocked storage cannot
          // prevent a successful import or cause an endless reload loop.
          try {
            sessionStorage.setItem(storageKey, 'true');
            window.location.reload();
            return;
          } catch (_) {}
        }
        reject(error);
      });
  });
}

const RosterManagement = lazy(() => lazyRetry(() => import('./pages/RosterManagement'), 'roster-management'));
const TransferMarket = lazy(() => lazyRetry(() => import('./pages/TransferMarket'), 'transfer-market'));
const Summary = lazy(() => lazyRetry(() => import('./pages/Summary'), 'summary'));
const TransferBuilder = lazy(() => lazyRetry(() => import('./pages/TransferBuilder'), 'transfer-builder'));
const TransferWindowChallenge = lazy(() => lazyRetry(() => import('./pages/TransferWindowChallenge'), 'transfer-window-challenge'));
const LineupBuilder = lazy(() => lazyRetry(() => import('./pages/LineupBuilder'), 'lineup-builder'));
const TacticsBoardWrapper = lazy(() => lazyRetry(() => import('./pages/TacticsBoardWrapper'), 'tactics-board'));
const Analyst = lazy(() => lazyRetry(() => import('./pages/Analyst'), 'analyst'));
const TermsAndConditions = lazy(() => lazyRetry(() => import('./pages/TermsAndConditions'), 'terms-and-conditions'));
const PrivacyPolicy = lazy(() => lazyRetry(() => import('./pages/PrivacyPolicy'), 'privacy-policy'));
const About = lazy(() => lazyRetry(() => import('./pages/About'), 'about'));
const ThirtyEightO = lazy(() => lazyRetry(() => import('./pages/ThirtyEightO'), '38-0'));
const ThirtyEightOPlay = lazy(() => lazyRetry(() => import('./pages/ThirtyEightOPlay'), '38-0-play'));
const ThirtyEightOGauntlet = lazy(() => lazyRetry(() => import('./pages/ThirtyEightOGauntlet'), '38-0-gauntlet'));
const ThirtyEightOGauntletDaily = lazy(() => lazyRetry(() => import('./pages/ThirtyEightOGauntletDaily'), '38-0-gauntlet-daily'));
const ThirtyEightOPackChallenge = lazy(() => lazyRetry(() => import('./pages/ThirtyEightOPackChallenge'), '38-0-packs'));
const ThirtyEightODaily = lazy(() => lazyRetry(() => import('./pages/ThirtyEightODaily'), '38-0-daily'));
const ChampionsXIHub = lazy(() => lazyRetry(() => import('./pages/ChampionsXIHub'), 'champions-xi-hub'));
const ChampionsXIPlay = lazy(() => lazyRetry(() => import('./pages/ChampionsXIPlay'), 'champions-xi-play'));
const ChampionsXIDaily = lazy(() => lazyRetry(() => import('./pages/ChampionsXIDaily'), 'champions-xi-daily'));
const WorldXICupHub = lazy(() => lazyRetry(() => import('./pages/WorldXICupHub'), 'world-xi-cup-hub'));
const WorldXICupClassic = lazy(() => lazyRetry(() => import('./pages/WorldXICupClassic'), 'world-xi-cup-classic'));
const WorldXICupDaily = lazy(() => lazyRetry(() => import('./pages/WorldXICupDaily'), 'world-xi-cup-daily'));
const WorldXICupPackDraft = lazy(() => lazyRetry(() => import('./pages/WorldXICupPackDraft'), 'world-xi-cup-pack-draft'));
const WorldXICupLegendPreview = lazy(() => lazyRetry(() => import('./pages/WorldXICupLegendPreview'), 'world-xi-cup-legend-preview'));
const WorldXICupPerfectResultPreview = lazy(() => lazyRetry(() => import('./pages/WorldXICupPerfectResultPreview'), 'world-xi-cup-perfect-result-preview'));
const SquadverseImposter = lazy(() => lazyRetry(() => import('./pages/SquadverseImposter'), 'squadverse-imposter'));
const SquadverseAlphabet = lazy(() => lazyRetry(() => import('./pages/SquadverseAlphabet'), 'squadverse-alphabet'));
const SquadverseCardClash = lazy(() => lazyRetry(() => import('./pages/SquadverseCardClash'), 'squadverse-card-clash'));
const AuctionDraft = lazy(() => lazyRetry(() => import('./pages/AuctionDraft'), 'auction-draft'));
const AuctionDraftBalanceHarness = lazy(() => lazyRetry(() => import('./pages/AuctionDraftBalanceHarness'), 'auction-draft-balance-harness'));
const SquadverseCardClashCup = lazy(() => lazyRetry(() => import('./pages/SquadverseCardClashCup'), 'squadverse-card-clash-cup'));
const SquadverseCardClashDaily = lazy(() => lazyRetry(() => import('./pages/SquadverseCardClashDaily'), 'squadverse-card-clash-daily'));
const SquadverseCardClashClassic = lazy(() => lazyRetry(() => import('./pages/SquadverseCardClashClassic'), 'squadverse-card-clash-classic'));
const SquadverseCardClashDeckDebug = lazy(() => lazyRetry(() => import('./pages/SquadverseCardClashDeckDebug'), 'squadverse-card-clash-decks'));
const SquadverseCardClashReview = lazy(() => lazyRetry(() => import('./pages/SquadverseCardClashReview'), 'squadverse-card-clash-review'));
const SquadBuilderChallenge = lazy(() => lazyRetry(() => import('./pages/SquadBuilderChallenge'), 'squad-builder-challenge'));
const SquadBuilderChallengeDaily = lazy(() => lazyRetry(() => import('./pages/SquadBuilderChallengeDaily'), 'squad-builder-challenge-daily'));
const BuyKeepSell = lazy(() => lazyRetry(() => import('./pages/BuyKeepSell'), 'buy-keep-sell'));
const BudgetSquadSetup = lazy(() => lazyRetry(() => import('./pages/BudgetSquadSetup'), 'budget-squad-setup'));
const BudgetSquadBuild = lazy(() => lazyRetry(() => import('./pages/BudgetSquadBuild'), 'budget-squad-build'));
const BudgetSquadDaily = lazy(() => lazyRetry(() => import('./pages/BudgetSquadDaily'), 'budget-squad-daily'));
const StartBenchSell = lazy(() => lazyRetry(() => import('./pages/StartBenchSell'), 'start-bench-sell'));
const TierListPage = lazy(() => lazyRetry(() => import('./pages/TierListPage'), 'tier-list'));
const CreateTierListPage = lazy(() => lazyRetry(() => import('./pages/CreateTierListPage'), 'tier-list-create'));
const CustomTierListPage = lazy(() => lazyRetry(() => import('./pages/CustomTierListPage'), 'tier-list-custom'));
const SharedTierListPage = lazy(() => lazyRetry(() => import('./pages/SharedTierListPage'), 'tier-list-share'));
const NotFound = lazy(() => lazyRetry(() => import('./pages/NotFound'), 'not-found'));

const Loading = () => (
  <div className="app-loading" role="status" aria-live="polite">
    Loading…
  </div>
);

function RouteDefaultSeo() {
  const location = useLocation();
  const normalizedPath = normalizeIndexingPath(location.pathname);
  const url = `${SITE_URL}${normalizedPath}`;
  const shouldNoIndex = isNoIndexRoute(normalizedPath);

  return (
    <Helmet>
      <meta property="og:url" content={url} />
      {shouldNoIndex && <meta name="robots" content="noindex,follow" />}
      {!shouldNoIndex && <link rel="canonical" href={url} />}
    </Helmet>
  );
}

function AnalyticsPageViews() {
  const location = useLocation();
  const pathname = location.pathname || '/';
  const lastTrackedPathRef = React.useRef(null);

  const sendPageView = React.useCallback(() => {
    if (lastTrackedPathRef.current === pathname) return;
    if (trackGaPageView(pathname)) lastTrackedPathRef.current = pathname;
  }, [pathname]);

  React.useEffect(() => {
    sendPageView();
  }, [sendPageView]);

  React.useEffect(() => {
    const onConsentChange = (event) => {
      if (event.detail?.consent === 'denied') lastTrackedPathRef.current = null;
      if (event.detail?.consent === 'granted') sendPageView();
    };
    const onAnalyticsReady = () => sendPageView();
    window.addEventListener('squadverse:analytics-consent', onConsentChange);
    window.addEventListener('squadverse:analytics-ready', onAnalyticsReady);
    return () => {
      window.removeEventListener('squadverse:analytics-consent', onConsentChange);
      window.removeEventListener('squadverse:analytics-ready', onAnalyticsReady);
    };
  }, [sendPageView]);

  return null;
}

function App() {
  const balanceHarnessEnabled = process.env.NODE_ENV !== 'production'
    && process.env.REACT_APP_AUCTION_DRAFT_BALANCE_HARNESS === '1';

  return (
    <>
      <RouteDefaultSeo />
      <AnalyticsPageViews />
      <Helmet>
        <title>Squadverse - Free Football Squad Games &amp; Daily Challenges</title>
        <meta name="description" content="Football squad builders, daily draft games, transfer tools, ranking debates, and shareable result graphics." />
        <meta property="og:title" content="Squadverse - Free Football Squad Games & Daily Challenges" />
        <meta property="og:description" content="Football squad builders, daily draft games, transfer tools, ranking debates, and shareable result graphics." />
        <meta property="og:image" content="https://www.squadverse.uk/og-image.png" />
        <meta property="og:image:alt" content="Squadverse - football squad games and daily challenges" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Squadverse" />
        <meta property="og:locale" content="en_GB" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Squadverse - Free Football Squad Games & Daily Challenges" />
        <meta name="twitter:description" content="Football squad builders, daily draft games, transfer tools, ranking debates, and shareable result graphics." />
        <meta name="twitter:image" content="https://www.squadverse.uk/og-image.png" />
      </Helmet>
      <div className="app-shell">
        <a className="app-skip-link" href="#main-content">Skip to main content</a>
        <div id="main-content" className="app-route-content" tabIndex="-1">
          <Suspense fallback={<Loading />}>
            <Routes>
              <Route path="/" element={<HomePage />} />
            <Route path="/transfer-builder-setup" element={<TeamSelection />} />
            <Route path="/roster-management" element={<RosterManagement />} />
            <Route path="/transfer-market" element={<TransferMarket />} />
            <Route path="/summary" element={<Summary />} />
            <Route path="/transfer-builder" element={<TransferBuilder />} />
            <Route path="/transfer-window-challenge" element={<TransferWindowChallenge />} />
            <Route path="/lineup-builder" element={<LineupBuilder />} />
            <Route path="/tactics-board" element={<TacticsBoardWrapper />} />
            <Route path="/analyst" element={<Analyst />} />
            <Route path="/analyst/:leagueId/:seasonId" element={<Analyst />} />
            <Route path="/analyst/:leagueId/:seasonId/:playerSlug" element={<Analyst />} />
            <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/about" element={<About />} />
            <Route path="/38-0" element={<ThirtyEightO />} />
            <Route path="/38-0/play" element={<ThirtyEightOPlay />} />
            <Route path="/38-0/gauntlet" element={<ThirtyEightOGauntlet />} />
            <Route path="/38-0/gauntlet/daily" element={<ThirtyEightOGauntletDaily />} />
            <Route path="/38-0/packs" element={<ThirtyEightOPackChallenge />} />
            <Route path="/38-0/daily" element={<ThirtyEightODaily />} />
            <Route path="/champions-xi" element={<ChampionsXIHub />} />
            <Route path="/champions-xi/play" element={<ChampionsXIPlay />} />
            <Route path="/champions-xi/daily" element={<ChampionsXIDaily />} />
            <Route path="/world-xi-cup" element={<WorldXICupHub />} />
            <Route path="/world-xi-cup/classic" element={<WorldXICupClassic />} />
            <Route path="/world-xi-cup/daily" element={<WorldXICupDaily />} />
            <Route path="/world-xi-cup/pack-draft" element={<WorldXICupPackDraft />} />
            <Route path="/world-xi-cup/legend-preview" element={<WorldXICupLegendPreview />} />
            <Route path="/world-xi-cup/perfect-result-preview" element={<WorldXICupPerfectResultPreview />} />
            <Route path="/world-cup-knockout-predictor" element={<Navigate to="/" replace />} />
            <Route path="/imposter" element={<SquadverseImposter />} />
            <Route path="/alphabet" element={<SquadverseAlphabet />} />
            <Route path="/card-clash" element={<SquadverseCardClash />} />
            <Route path="/auction-draft" element={<AuctionDraft />} />
             {balanceHarnessEnabled && <Route path="/__test/auction-draft-balance" element={<AuctionDraftBalanceHarness />} />}
            <Route path="/card-clash/cup" element={<SquadverseCardClashCup />} />
            <Route path="/card-clash/daily" element={<SquadverseCardClashDaily />} />
            <Route path="/card-clash/classic" element={<SquadverseCardClashClassic />} />
            <Route path="/card-clash/decks" element={<SquadverseCardClashDeckDebug />} />
            <Route path="/card-clash/debug-decks" element={<SquadverseCardClashDeckDebug />} />
            <Route path="/card-clash/review" element={<SquadverseCardClashReview />} />
            <Route path="/squad-builder" element={<SquadBuilderChallenge />} />
            <Route path="/squad-builder/daily" element={<SquadBuilderChallengeDaily />} />
            <Route path="/buy-keep-sell" element={<BuyKeepSell />} />
            <Route path="/budget-squad" element={<BudgetSquadSetup />} />
            <Route path="/budget-squad/build" element={<BudgetSquadBuild />} />
            <Route path="/budget-squad/daily" element={<BudgetSquadDaily />} />
            <Route path="/start-bench-sell" element={<StartBenchSell />} />
            <Route path="/tier-list" element={<TierListPage />} />
            <Route path="/tier-list/create" element={<CreateTierListPage />} />
            <Route path="/tier-list/custom/:customTemplateId" element={<CustomTierListPage />} />
            <Route path="/tier-list/share/:submissionId" element={<SharedTierListPage />} />
            <Route path="/tier-list/:templateSlug" element={<TierListPage />} />
            <Route path="/world-xi-cup-26" element={<Navigate to="/world-xi-cup/pack-draft" replace />} />
            <Route path="/world-xi-cup-26/legend-preview" element={<Navigate to="/world-xi-cup/legend-preview" replace />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </div>
        <BugReportButton />
        <CoffeeSupportBanner />
        <AnalyticsConsent />
        <Analytics />
      </div>
    </>
  );
}

export default App;
